import { FileSystemFileEntry } from 'ngx-file-drop';

export function isValidDate(date: Date) {
  return date instanceof Date && !isNaN(date.getTime());
}

export function splitBankAccountAndName(bankDetails) {
  const seperatorIndex = bankDetails.indexOf("-");
  const accountNumber = bankDetails.substring(0, seperatorIndex);
  const bankName = bankDetails.substring(seperatorIndex + 1);
  return { field: accountNumber, field1: bankName };
}

export function getDateDiff(dateA: Date, dateB: Date): number {
  const newDateA = new Date(dateA);
  const newDateB = new Date(dateB);
  return (newDateA.setHours(0, 0, 0, 0) - newDateB.setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24);
}

export function sortOrgName(a: any, b: any) {
  if (a.name && !b.name) return -1;
  if (b.name && !a.name) return 1;
  return a.name.localeCompare(b.name);
}

export function setUTCHours(date: Date | string, dateType: string): string {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  // Handle Moment.js objects returned by Angular Material datepicker
  if (date && typeof (date as any).toDate === 'function') {
    date = (date as any).toDate();
  }
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  // removing timezone offset from given date
  if (date.getHours() !== 0) {
    date.setHours(0, 0, 0, 0);
  }
  const timeAfterRemovingOffSet = date.getTime() - (date.getTimezoneOffset() * 60 * 1000);
  date = new Date(timeAfterRemovingOffSet);
  if (dateType === 'end') {
    // adding 1 day and removing 1 unit to get in format: 23:59:59:999
    date.setDate(date.getDate() + 1);
    date.setTime(date.getTime() - 1);
  }
  return date?.toISOString();
}

export function getUTCDate(date: Date | string, isTimezoneDependent = true): Date {
  if (date === null) {
    return null;
  }
  let localUTCDate = new Date(date);
  if (isNaN(localUTCDate.getDate())) {
    return null;
  }
  // skip setting hours in case date (end date) is already in utc format.
  if (!(typeof (date) === 'string' && date.includes('23:59:59.999Z')) && isTimezoneDependent) {
    localUTCDate = new Date(setUTCHours(date, 'start'));
  }
  const localDate = localUTCDate.getUTCDate();
  const localMonth = localUTCDate.getUTCMonth();
  const localYear = localUTCDate.getUTCFullYear();
  const newDate = new Date(localYear, localMonth, localDate);
  return newDate;
}
export function dateDiffInDays(a, b) {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}
export function fileSelection(event: File | FileSystemFileEntry | any): any[] {
  const array: any = [];
  if (event.length) {
    event.filter((res) => {
      if (res.isFile) {
        res.file((fileInstance: File) => {
          array.push(fileInstance);
        });
      } else {
        array.push(res);
      }
    });
  }
  return array;
}

export function getCookie(name: string): string {
  if (document.cookie) {
    const ca: Array<string> = document.cookie.split(';');
    const caLen: number = ca.length;
    const cookieName = `${name}=`;
    let c: string;
    for (let i = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) == 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
  }
  return '';
}

export function getAndUpdateCookieInfo(name: string, type: string, updateVal?: string): string {
  if (document.cookie) {
    const ca: Array<string> = document.cookie.split(';');
    const cookieIndex = ca.findIndex((item) => {
      const cookieNameSplit = item.split('=')[0];
      return cookieNameSplit && cookieNameSplit.trim() === name;
    });
    if (cookieIndex !== -1) {
      if (type === 'get') {
        return ca[cookieIndex];
      } else {
        delete_cookie(name, '/', '.cargoes.com');
        const date = new Date();
        date.setDate(date.getDate() + 1);
        document.cookie = `${updateVal}; expires=${date}; path=/`;
      }
    }
  }
  return '';
}

export function delete_cookie(name: string, path: string, domain: string): void {
  document.cookie = `${name}=;path=${path};domain=${domain};expires=Thu, 01 Jan 1970 00:00:01 GMT`;
}

export function removeArrayDuplicates(sourceArr: any[], duplicateFilterKey: string): any[] {
  const tempObj = {};
  const sourceLen = sourceArr.length;
  for (let i = 0; i < sourceLen; i++) {
    tempObj[sourceArr[i][duplicateFilterKey]] = sourceArr[i];
  }
  sourceArr = Object.values(tempObj);
  return sourceArr;
}

export function formatDateToMMDDYYYY(date: Date) {
  const yyyy = date.getFullYear();
  let mm: any = date.getMonth() + 1; // Month start with 0
  let dd: any = date.getDate();
  dd = (dd < 10) ? ('0' + dd) : dd;
  mm = (mm < 10) ? ('0' + mm) : mm;
  return (mm + '/' + dd + '/' + yyyy);
}

/**
 * Some APIs return dates as 'dd-MM-yyyy' (e.g. '01-08-2026' = 1 Aug 2026).
 * new Date() reads that as MM-dd-yyyy and would render 8 Jan 2026, so parse the parts
 * explicitly. ISO strings and anything unrecognised are handed back to the Date ctor.
 */
export function parseDdMmYyyyDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string') return null;

  const ddMMyyyy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value.trim());
  if (ddMMyyyy) {
    const [, day, month, year] = ddMMyyyy;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  const fallback = new Date(value);
  return isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * A missing/unparseable createdDate means the doc was just uploaded in this session
 * and has not round-tripped through the backend yet — treat it as the newest.
 */
export function getCreatedTime(file: any): number {
  if (!file?.createdDate) return Number.MAX_SAFE_INTEGER;
  const time = new Date(file.createdDate).getTime();
  return isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
}

export function sortByCreatedDateDesc(files: any[]): any[] {
  return [...(files || [])].sort((a, b) => {
    const timeDiff = getCreatedTime(b) - getCreatedTime(a);
    if (timeDiff !== 0) return timeDiff;
    const refDiff = String(b?.documentReference ?? '').localeCompare(String(a?.documentReference ?? ''));
    if (refDiff) return refDiff;
    return (b?.id ?? Number.MAX_SAFE_INTEGER) - (a?.id ?? Number.MAX_SAFE_INTEGER);
  });
}

export function getLatestDocumentReferences(documentDetails: any[]): Set<string> {
  const docsByTypeAndName = new Map<string, any[]>();
  (documentDetails || []).forEach((doc) => {
    const documentType = doc?.documentType?.trim()?.toLowerCase() ?? '';
    const documentName = doc?.documentNumber ?? doc?.documentName ?? doc?.document_name ?? '';
    const key = JSON.stringify([documentType, documentName]);
    const docs = docsByTypeAndName.get(key) ?? [];
    docs.push(doc);
    docsByTypeAndName.set(key, docs);
  });
  const references = new Set<string>();
  docsByTypeAndName.forEach((docs) => references.add(sortByCreatedDateDesc(docs)[0]?.documentReference));
  return references;
}

export interface ChipTooltipConfigItem {
  label: string;
  key: string;
}

export const FLAG_CHIP_TOOLTIP_CONFIG: ChipTooltipConfigItem[] = [
  { label: 'Description', key: 'description' },
  { label: 'Created By', key: 'tooltipCreatedBy' },
  { label: 'Created At', key: 'tooltipCreatedAt' },
];

export function buildChipTooltip(option: any, chipTooltipConfig: ChipTooltipConfigItem[] | null): string {
  if (!chipTooltipConfig?.length || !option) return '';
  return chipTooltipConfig
    .filter(({ key }) => option[key] != null && option[key] !== '')
    .map(({ label, key }) => `${label}:\n${option[key]}`)
    .join('\n\n');
}

export function enrichFlagForTooltip(flag: any): any {
  if (!flag) return flag;
  const createdBy = flag.createdBy ?? flag.created_by;
  const createdByRole = flag.createdByRole ?? flag.created_by_role;
  const createdAt = flag.createdAt ?? flag.created_at;
  const enriched = {
    ...flag,
    name: flag.name ?? flag.value,
    description: flag.description || '',
    tooltipCreatedBy: flag.tooltipCreatedBy || [createdBy, createdByRole].filter(Boolean).join(' | '),
    tooltipCreatedAt: flag.tooltipCreatedAt || (createdAt ? formatDateToMMDDYYYY(getUTCDate(createdAt)) : ''),
  };
  enriched.chipTooltip = buildChipTooltip(enriched, FLAG_CHIP_TOOLTIP_CONFIG);
  return enriched;
}

export function isSafari(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent) {
    return userAgent?.indexOf('safari') != -1 && userAgent?.indexOf('chrome') === -1;
  }
  return false;
}

export function formatSummaryAmount(amount: string | number, currency?: string): string {
  if (amount === null || amount === undefined || amount === '') {
    return '--';
  }
  const value = Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${value}` : value;
}

// Add this helper alongside your other methods
export function excelSerialToDateStr(serial: number): string {
  // Excel's epoch is Dec 30 1899, and it has a leap-year bug for 1900
  // so we offset by 25569 days to Unix epoch (Jan 1 1970)
  const utcDays = serial - 25569;
  const utcMs = utcDays * 86400 * 1000;
  const date = new Date(utcMs);
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = date.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}